import { useState } from "react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ChevronDown,
  ChevronRight,
  MapPin,
  CheckCircle,
  FileLock2,
} from "lucide-react";
import type { OrgApprovedTimeEntry } from "../hooks/useApprovedTimeEntries";
import type { GroupedProvider } from "../utils/group-time-entries";
import { calculateHours, formatHours } from "../utils/group-time-entries";
import { TimeEntryOrgNote } from "./TimeEntryOrgNote";
import { AdHocEntryBadge } from "./AdHocEntryBadge";
import { EntryDifferenceBadge } from "./EntryDifferenceBadge";
import {
  NOT_SCHEDULED_LABEL,
  getEntryHoursDifference,
  isAdHocEntry,
} from "../utils/ad-hoc-entry";
import { PayableAmount } from "./time-entry-history-table/PayableAmount";
import { SourcePayoutStatus } from "@/features/billing/SourcePayoutStatus";

interface TimeEntryHistoryTableProps {
  groupedEntries: GroupedProvider<OrgApprovedTimeEntry>[];
  organizationBusinessUnitId: string;
  canEditOrgNotes: boolean;
  onOrgNoteSaved: () => void;
}

export function TimeEntryHistoryTable({
  groupedEntries,
  organizationBusinessUnitId,
  canEditOrgNotes,
  onOrgNoteSaved,
}: TimeEntryHistoryTableProps) {
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(
    new Set(groupedEntries.map((g) => g.providerId)),
  );

  const toggleProvider = (providerId: string) => {
    const newExpanded = new Set(expandedProviders);
    if (newExpanded.has(providerId)) {
      newExpanded.delete(providerId);
    } else {
      newExpanded.add(providerId);
    }
    setExpandedProviders(newExpanded);
  };

  const formatWageType = (wageType: string) => {
    return wageType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="space-y-4">
      {/* Provider groups */}
      {groupedEntries.map((provider) => {
        const isExpanded = expandedProviders.has(provider.providerId);

        return (
          <Card
            key={provider.providerId}
            className="overflow-hidden border-slate-200"
          >
            <Collapsible open={isExpanded}>
              <CollapsibleTrigger asChild>
                <div
                  className="flex items-center justify-between px-4 py-3 bg-slate-100 hover:bg-slate-200 transition-colors cursor-pointer"
                  onClick={() => toggleProvider(provider.providerId)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-slate-600" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-600" />
                    )}
                    <span className="font-semibold text-slate-900">
                      {provider.providerName}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {provider.totalEntries}{" "}
                      {provider.totalEntries === 1 ? "entry" : "entries"}
                    </Badge>
                  </div>
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent>
                {provider.facilities.map((facility) => {
                  return (
                    <div key={facility.facilityId} className="border-t">
                      {/* Facility header */}
                      <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          <div>
                            <div className="font-medium text-slate-800">
                              {facility.facilityName}
                            </div>
                            {facility.facilityLocation && (
                              <div className="text-xs text-slate-500">
                                {facility.facilityLocation}
                              </div>
                            )}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {facility.entries.length}{" "}
                          {facility.entries.length === 1 ? "shift" : "shifts"}
                        </Badge>
                      </div>

                      {/* Desktop table */}
                      <div className="hidden sm:block">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-white text-xs">
                              <TableHead>Date</TableHead>
                              <TableHead>Scheduled</TableHead>
                              <TableHead>Submitted</TableHead>
                              <TableHead>Difference</TableHead>
                              <TableHead>Wage Type</TableHead>
                              <TableHead>Approved</TableHead>
                              <TableHead>Payout</TableHead>
                              <TableHead>Org Note</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {facility.entries.map((entry) => {
                              const scheduledHours = calculateHours(
                                new Date(entry.scheduledStartsAt),
                                new Date(entry.scheduledEndsAt),
                              );
                              const submittedHours = calculateHours(
                                new Date(entry.submittedStartsAt),
                                new Date(entry.submittedEndsAt),
                              );
                              const difference = getEntryHoursDifference(entry);

                              return (
                                <TableRow
                                  key={entry.timeEntryId}
                                  className="hover:bg-slate-50"
                                >
                                  <TableCell>
                                    <div className="font-medium">
                                      {format(
                                        new Date(entry.scheduledStartsAt),
                                        "EEE, MMM d",
                                      )}
                                    </div>
                                    {isAdHocEntry(entry) && (
                                      <AdHocEntryBadge className="mt-1" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isAdHocEntry(entry) && (
                                      <div className="text-sm text-slate-500">
                                        {NOT_SCHEDULED_LABEL}
                                      </div>
                                    )}
                                    {!isAdHocEntry(entry) && (
                                      <div className="text-sm">
                                        <div className="text-slate-600">
                                          {format(
                                            new Date(entry.scheduledStartsAt),
                                            "h:mm a",
                                          )}{" "}
                                          -{" "}
                                          {format(
                                            new Date(entry.scheduledEndsAt),
                                            "h:mm a",
                                          )}
                                        </div>
                                        <div className="text-slate-400">
                                          {formatHours(scheduledHours)}
                                        </div>
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm">
                                      <div className="font-medium text-slate-900">
                                        {format(
                                          new Date(entry.submittedStartsAt),
                                          "h:mm a",
                                        )}{" "}
                                        -{" "}
                                        {format(
                                          new Date(entry.submittedEndsAt),
                                          "h:mm a",
                                        )}
                                      </div>
                                      <div className="text-blue-600">
                                        {formatHours(submittedHours)}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <EntryDifferenceBadge
                                      difference={difference}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm">
                                      <div>
                                        {formatWageType(entry.baseWageType)}
                                      </div>
                                      {entry.isOvertime && (
                                        <Badge
                                          variant="outline"
                                          className="text-xs mt-1 bg-blue-50 text-blue-700 border-blue-200"
                                        >
                                          Overtime
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1.5 text-sm text-green-600">
                                      <CheckCircle className="h-4 w-4" />
                                      <span>
                                        {format(
                                          new Date(entry.approvedAt),
                                          "MMM d",
                                        )}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="space-y-1">
                                      <PayableAmount entry={entry} />
                                      <SourcePayoutStatus
                                        payoutId={entry.payoutId}
                                        payoutStatus={entry.payoutStatus}
                                        paidAt={entry.paidAt}
                                      />
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <TimeEntryOrgNote
                                      timeEntryId={entry.timeEntryId}
                                      organizationBusinessUnitId={
                                        organizationBusinessUnitId
                                      }
                                      orgNote={entry.orgNote}
                                      canEdit={canEditOrgNotes}
                                      popover
                                      onSaved={onOrgNoteSaved}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Mobile cards */}
                      <div className="sm:hidden space-y-2 p-3">
                        {facility.entries.map((entry) => {
                          const scheduledHours = calculateHours(
                            new Date(entry.scheduledStartsAt),
                            new Date(entry.scheduledEndsAt),
                          );
                          const submittedHours = calculateHours(
                            new Date(entry.submittedStartsAt),
                            new Date(entry.submittedEndsAt),
                          );
                          const difference = getEntryHoursDifference(entry);

                          return (
                            <Card key={entry.timeEntryId} className="p-3">
                              <CardContent className="p-0 space-y-3">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <div className="font-medium">
                                      {format(
                                        new Date(entry.scheduledStartsAt),
                                        "EEEE, MMM d",
                                      )}
                                    </div>
                                    {isAdHocEntry(entry) && (
                                      <AdHocEntryBadge className="mt-1" />
                                    )}
                                  </div>
                                  <EntryDifferenceBadge
                                    difference={difference}
                                    className="text-xs"
                                  />
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <div className="text-xs text-slate-500 mb-1">
                                      Scheduled
                                    </div>
                                    {isAdHocEntry(entry) && (
                                      <div className="text-slate-500">
                                        {NOT_SCHEDULED_LABEL}
                                      </div>
                                    )}
                                    {!isAdHocEntry(entry) && (
                                      <>
                                        <div className="text-slate-600">
                                          {format(
                                            new Date(entry.scheduledStartsAt),
                                            "h:mm a",
                                          )}{" "}
                                          -{" "}
                                          {format(
                                            new Date(entry.scheduledEndsAt),
                                            "h:mm a",
                                          )}
                                        </div>
                                        <div className="text-slate-400">
                                          {formatHours(scheduledHours)}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  <div>
                                    <div className="text-xs text-slate-500 mb-1">
                                      Submitted
                                    </div>
                                    <div className="font-medium text-slate-900">
                                      {format(
                                        new Date(entry.submittedStartsAt),
                                        "h:mm a",
                                      )}{" "}
                                      -{" "}
                                      {format(
                                        new Date(entry.submittedEndsAt),
                                        "h:mm a",
                                      )}
                                    </div>
                                    <div className="text-blue-600">
                                      {formatHours(submittedHours)}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between pt-2 border-t">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm">
                                      {formatWageType(entry.baseWageType)}
                                    </span>
                                    {entry.isOvertime && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                                      >
                                        OT
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5 text-sm text-green-600">
                                    <CheckCircle className="h-4 w-4" />
                                    <span>
                                      Approved{" "}
                                      {format(
                                        new Date(entry.approvedAt),
                                        "MMM d",
                                      )}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex items-start justify-between gap-3 border-t pt-2">
                                  <span className="text-xs font-medium text-slate-500">
                                    Payout
                                  </span>
                                  <div className="flex flex-col items-end gap-1">
                                    <PayableAmount entry={entry} />
                                    <SourcePayoutStatus
                                      payoutId={entry.payoutId}
                                      payoutStatus={entry.payoutStatus}
                                      paidAt={entry.paidAt}
                                    />
                                  </div>
                                </div>

                                {entry.providerNote && (
                                  <div className="pt-2 border-t text-sm">
                                    <div className="text-xs text-slate-500 mb-1">
                                      Provider Note
                                    </div>
                                    <div className="text-slate-600">
                                      {entry.providerNote}
                                    </div>
                                  </div>
                                )}
                                {(canEditOrgNotes || entry.orgNote) && (
                                  <div className="pt-2 border-t text-sm">
                                    <div className="mb-1 flex items-center gap-1 text-xs font-medium text-slate-500">
                                      <FileLock2 className="h-3.5 w-3.5" />
                                      Org Note
                                    </div>
                                    <TimeEntryOrgNote
                                      timeEntryId={entry.timeEntryId}
                                      organizationBusinessUnitId={
                                        organizationBusinessUnitId
                                      }
                                      orgNote={entry.orgNote}
                                      canEdit={canEditOrgNotes}
                                      popover
                                      onSaved={onOrgNoteSaved}
                                    />
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>

                      {/* Provider notes section (desktop) */}
                      {facility.entries.some((e) => e.providerNote) && (
                        <div className="hidden sm:block px-4 py-3 bg-slate-50 border-t">
                          <div className="text-xs font-semibold text-slate-500 mb-2">
                            Provider Notes:
                          </div>
                          {facility.entries
                            .filter((e) => e.providerNote)
                            .map((entry) => (
                              <div
                                key={entry.timeEntryId}
                                className="text-sm text-slate-600 mb-1"
                              >
                                <span className="font-medium">
                                  {format(
                                    new Date(entry.scheduledStartsAt),
                                    "MMM d",
                                  )}
                                  :
                                </span>{" "}
                                {entry.providerNote}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}
    </div>
  );
}
